import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';

import { catalogs, integrations, type Address } from '@/shared/api/resources';
import { formatUf, SIGLAS_DAS_UFS } from '@/shared/lib/format';

import { Button } from './Button';
import { Field } from './Field';
import { FieldLabel } from './FieldLabel';

/** O catalogo muda por migration, e nao durante o uso: uma leitura por sessao basta. */
const UMA_HORA = 60 * 60 * 1000;

const classeDaSelecao =
  'h-10 w-full rounded-md border border-line-strong bg-surface px-3 text-sm text-ink-900 focus:border-brand-500 focus:outline-none focus:ring-3 focus:ring-brand-500/15 disabled:bg-surface-sunken disabled:text-ink-500';

/** Compara nomes sem acento e sem caixa, como a busca do catalogo. */
function chaveDoNome(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

export interface AddressFieldsProps {
  value: Address;
  onChange: (address: Address) => void;
  /** Marca CEP, logradouro, numero, bairro, cidade e UF como obrigatorios. */
  required?: boolean | undefined;
  legend?: string | undefined;
  disabled?: boolean | undefined;
  /**
   * Grava tambem o codigo IBGE do municipio em `city_code` (P-TOM-C1). So o
   * cadastro que guarda o codigo liga: o contrato do corretor e das partes da CCG
   * nao tem o campo, e o servidor recusaria o campo desconhecido.
   */
  withCityCode?: boolean | undefined;
}

/**
 * Endereco comum de corretor, tomador, filial, partes da CCG e dos cadastros que
 * vierem, com a mesma disposicao em todos (P-FUN-2b).
 *
 * - CEP: "Buscar CEP" consulta `GET /api/v1/address-lookup`, que existia sem nenhum
 *   formulario chamando (Anexo C do 47, "Corretora, tomador..." 10). Ao sair do campo
 *   com oito digitos e o logradouro vazio, a consulta corre sozinha. Ela so preenche:
 *   quem confere e salva e a pessoa, e sem a consulta o endereco segue digitado;
 * - UF: a lista vem do catalogo do IBGE (P-FUN-1), com o nome por extenso. Se o
 *   catalogo nao responder, a lista fixa das 27 siglas segura o formulario;
 * - Cidade: os municipios da UF escolhida, do mesmo catalogo. A cidade gravada que
 *   nao esta na lista (cadastro antigo, grafia sem acento) continua aparecendo, como
 *   foi gravada, e so muda se a pessoa escolher outra. Sem catalogo, o campo volta a
 *   ser texto livre.
 *
 * O endereco gravado continua sendo a sigla da UF e o nome da cidade: o formato do
 * contrato nao muda, e nenhum cadastro antigo precisa de migracao para abrir. Com
 * `withCityCode`, o codigo IBGE do municipio escolhido vai junto em `city_code`; a
 * cidade digitada, sem catalogo, vai sem codigo, e o servidor a procura pelo nome.
 */
export function AddressFields({
  value,
  onChange,
  required,
  legend = 'Endereco',
  disabled,
  withCityCode,
}: AddressFieldsProps) {
  const ufId = useId();
  const cidadeId = useId();
  const [buscando, setBuscando] = useState(false);
  const [avisoCep, setAvisoCep] = useState<string | undefined>(undefined);
  const [consultaFeita, setConsultaFeita] = useState<string | undefined>(undefined);
  // O valor mais recente, para que a consulta que volta depois de outra digitacao nao
  // apague o que a pessoa escreveu enquanto esperava.
  const atual = useRef(value);
  useEffect(() => {
    atual.current = value;
  });

  const estados = useQuery({
    queryKey: ['catalogs', 'states'],
    queryFn: ({ signal }) => catalogs.states(signal),
    staleTime: UMA_HORA,
  });

  const uf = value.state.trim().toUpperCase();
  const municipios = useQuery({
    queryKey: ['catalogs', 'cities', uf],
    queryFn: ({ signal }) => catalogs.cities(uf, signal),
    enabled: uf.length === 2,
    staleTime: UMA_HORA,
  });

  function set(field: keyof Address, fieldValue: string) {
    onChange({ ...value, [field]: fieldValue });
  }

  /** O codigo IBGE da cidade escolhida, quando o formulario o grava. */
  function codigoDa(
    nome: string,
    lista: { name: string; ibge_code: string }[] | undefined,
  ): Partial<Address> {
    if (!withCityCode) return {};
    return {
      city_code: lista?.find((cidade) => cidade.name === nome)?.ibge_code ?? '',
    };
  }

  function setCidade(
    nome: string,
    lista: { name: string; ibge_code: string }[] | undefined,
  ) {
    onChange({ ...value, city: nome, ...codigoDa(nome, lista) });
  }

  /** O nome do catalogo para a cidade devolvida pela consulta, quando ha. */
  function nomeDoCatalogo(nome: string, lista: { name: string }[] | undefined): string {
    if (!nome || !lista) return nome;
    const chave = chaveDoNome(nome);
    return lista.find((cidade) => chaveDoNome(cidade.name) === chave)?.name ?? nome;
  }

  async function buscarCep(automatica = false) {
    const cep = value.zip_code.replace(/\D/g, '');
    if (cep.length !== 8) {
      if (!automatica) setAvisoCep('Informe o CEP com oito digitos para consultar.');
      return;
    }
    setBuscando(true);
    setAvisoCep(undefined);
    try {
      const achado = await integrations.lookupAddress(cep);
      setConsultaFeita(cep);
      const estado = (achado.state || atual.current.state).toUpperCase();
      let cidade = achado.city || atual.current.city;
      if (achado.city && estado.length === 2) {
        // A lista da UF achada costuma ja estar no cache; se nao estiver, o nome da
        // consulta fica como veio, que e o mesmo do IBGE na quase totalidade.
        cidade = nomeDoCatalogo(
          cidade,
          municipios.data && estado === uf ? municipios.data : undefined,
        );
      }
      onChange({
        ...atual.current,
        zip_code: achado.zip_code || atual.current.zip_code,
        street: achado.street || atual.current.street,
        district: achado.district || atual.current.district,
        city: cidade,
        state: estado,
        // O codigo so sai da lista ja carregada da mesma UF. Sem ela, vai vazio, e o
        // servidor procura o municipio pelo nome.
        ...codigoDa(
          cidade,
          municipios.data && estado === uf ? municipios.data : undefined,
        ),
      });
    } catch (erro) {
      setAvisoCep(
        erro instanceof Error
          ? `Consulta indisponivel: ${erro.message}`
          : 'Consulta indisponivel.',
      );
    } finally {
      setBuscando(false);
    }
  }

  // Opcoes de UF: o catalogo, ou a lista fixa quando ele nao responde. A sigla
  // gravada que nao esta em nenhuma das duas continua visivel.
  const opcoesDeUf = (
    estados.data?.map((estado) => ({ code: estado.code, name: estado.name })) ??
    SIGLAS_DAS_UFS.map((sigla) => ({ code: sigla, name: formatUf(sigla) }))
  ).slice();
  if (uf && !opcoesDeUf.some((opcao) => opcao.code === uf)) {
    opcoesDeUf.push({ code: uf, name: 'como gravado' });
  }

  const listaDeCidades = municipios.data;
  const cidadeNaLista =
    !value.city ||
    (listaDeCidades?.some((cidade) => cidade.name === value.city) ?? false);
  const cidadeComoLista = uf.length === 2 && listaDeCidades !== undefined;

  return (
    <fieldset className="flex flex-col gap-4" disabled={disabled}>
      <legend className="text-xs font-semibold text-ink-700">{legend}</legend>

      <div className="grid gap-4 sm:grid-cols-4">
        <div className="flex flex-col gap-1.5">
          <Field
            label="CEP"
            help="Com oito digitos, Buscar CEP preenche logradouro, bairro, cidade e UF. Confira antes de salvar; sem a consulta, digite o endereco."
            required={required}
            inputMode="numeric"
            autoComplete="postal-code"
            value={value.zip_code}
            error={avisoCep}
            onChange={(event) => {
              setAvisoCep(undefined);
              set('zip_code', event.target.value);
            }}
            onBlur={() => {
              const cep = value.zip_code.replace(/\D/g, '');
              if (cep.length === 8 && !value.street.trim() && consultaFeita !== cep) {
                void buscarCep(true);
              }
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            loading={buscando}
            onClick={() => void buscarCep()}
            className="self-start"
          >
            Buscar CEP
          </Button>
        </div>
        <div className="sm:col-span-2">
          <Field
            label="Logradouro"
            required={required}
            autoComplete="address-line1"
            value={value.street}
            onChange={(event) => set('street', event.target.value)}
          />
        </div>
        <Field
          label="Numero"
          required={required}
          value={value.number}
          onChange={(event) => set('number', event.target.value)}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-4">
        <Field
          label="Complemento"
          value={value.complement ?? ''}
          onChange={(event) => set('complement', event.target.value)}
        />
        <Field
          label="Bairro"
          required={required}
          value={value.district}
          onChange={(event) => set('district', event.target.value)}
        />
        <div className="flex flex-col gap-1.5">
          <FieldLabel
            htmlFor={ufId}
            label="UF"
            help="Lista das 27 unidades da federacao, do catalogo do IBGE."
            className="text-xs font-semibold text-ink-700"
          />
          <select
            id={ufId}
            required={required}
            value={uf}
            onChange={(event) =>
              // Trocar a UF limpa a cidade: o municipio pertence a UF, e manter o da
              // anterior gravaria um endereco que nao existe.
              onChange({
                ...value,
                state: event.target.value,
                city: '',
                ...(withCityCode ? { city_code: '' } : {}),
              })
            }
            className={classeDaSelecao}
          >
            <option value="" />
            {opcoesDeUf.map((opcao) => (
              <option key={opcao.code} value={opcao.code}>
                {`${opcao.code} - ${opcao.name}`}
              </option>
            ))}
          </select>
        </div>
        {cidadeComoLista ? (
          <div className="flex flex-col gap-1.5">
            <FieldLabel
              htmlFor={cidadeId}
              label="Cidade"
              help="Municipios da UF escolhida, do catalogo do IBGE."
              className="text-xs font-semibold text-ink-700"
            />
            <select
              id={cidadeId}
              required={required}
              value={value.city}
              onChange={(event) => setCidade(event.target.value, listaDeCidades)}
              className={classeDaSelecao}
            >
              <option value="" />
              {!cidadeNaLista ? (
                <option value={value.city}>{`${value.city} (como gravado)`}</option>
              ) : null}
              {listaDeCidades.map((cidade) => (
                <option key={cidade.ibge_code} value={cidade.name}>
                  {cidade.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <Field
            label="Cidade"
            help={
              uf.length === 2 && municipios.isPending
                ? 'Carregando os municipios da UF.'
                : 'Escolha a UF para ver a lista de municipios; sem a lista, digite o nome.'
            }
            required={required}
            value={value.city}
            onChange={(event) => setCidade(event.target.value, undefined)}
          />
        )}
      </div>
    </fieldset>
  );
}
